<script setup>
import { ref, computed, onMounted } from 'vue';
import { required, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

defineProps({
  hasAccounts: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['closeAccountCreateModal']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const accountName = ref('');

const rules = {
  accountName: {
    required,
    minLength: minLength(1),
  },
};

const v$ = useVuelidate(rules, { accountName });

const uiFlags = useMapGetter('agents/getUIFlags');
const isCreating = computed(() => uiFlags.value.isCreating);

onMounted(() => {
  dialogRef.value?.open();
});

const addAccount = async () => {
  try {
    const account_id = await store.dispatch('accounts/create', {
      account_name: accountName.value,
    });
    emit('closeAccountCreateModal');
    useAlert(t('CREATE_ACCOUNT.API.SUCCESS_MESSAGE'));
    window.location = `/app/accounts/${account_id}/dashboard`;
  } catch (error) {
    if (error.response.status === 422) {
      useAlert(t('CREATE_ACCOUNT.API.EXIST_MESSAGE'));
    } else {
      useAlert(t('CREATE_ACCOUNT.API.ERROR_MESSAGE'));
    }
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('CREATE_ACCOUNT.NEW_ACCOUNT')"
    :description="$t('CREATE_ACCOUNT.SELECTOR_SUBTITLE')"
    :confirm-button-label="$t('CREATE_ACCOUNT.FORM.SUBMIT')"
    :cancel-button-label="$t('CREATE_ACCOUNT.FORM.CANCEL')"
    :disable-confirm-button="v$.accountName.$invalid || isCreating"
    :is-loading="isCreating"
    @confirm="addAccount"
    @close="emit('closeAccountCreateModal')"
  >
    <div class="flex flex-col w-full gap-4">
      <div v-if="!hasAccounts" class="text-sm">
        <div class="flex items-center rounded-md alert">
          <div class="ml-1 mr-3">
            <fluent-icon icon="warning" />
          </div>
          {{ $t('CREATE_ACCOUNT.NO_ACCOUNT_WARNING') }}
        </div>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.accountName.$error }">
          {{ $t('CREATE_ACCOUNT.FORM.NAME.LABEL') }}
          <input
            v-model="accountName"
            type="text"
            :placeholder="$t('CREATE_ACCOUNT.FORM.NAME.PLACEHOLDER')"
            @input="v$.accountName.$touch"
          />
        </label>
      </div>
    </div>
  </Dialog>
</template>
